// PathExplorer.jsx - Fixed to show next scenarios and persistent injects
import React, { useState, useEffect } from 'react';

const PathExplorer = ({ blocks, simulationRunning, currentTime, onPathStep }) => {
  const [currentPath, setCurrentPath] = useState([]);
  const [currentBlock, setCurrentBlock] = useState(null);
  const [pathHistory, setPathHistory] = useState([]);
  const [persistentInjects, setPersistentInjects] = useState([]);
  const [nextOptions, setNextOptions] = useState([]);
  const [currentBlockProgress, setCurrentBlockProgress] = useState(0);

  // Find starting blocks
  const findStartingBlocks = () => {
    const targetIds = new Set();
    blocks.forEach(block => {
      if (block.type === 'event' && block.next) {
        block.next.forEach(targetId => targetIds.add(targetId));
      } else if (block.type === 'choice' && block.choices) {
        block.choices.forEach(choice => {
          if (choice.targetId) targetIds.add(choice.targetId);
        });
      }
    });
    return blocks.filter(block => !targetIds.has(block.id));
  };

  // Initialize with starting block(s)
  useEffect(() => {
    const startBlocks = findStartingBlocks();
    if (startBlocks.length > 0 && currentPath.length === 0) {
      const firstBlock = startBlocks[0];
      setCurrentBlock(firstBlock);
      setCurrentPath([firstBlock.id]);
      setPathHistory([{
        type: 'start',
        block: firstBlock,
        timestamp: 0,
        startTime: currentTime
      }]);
      updateNextOptions(firstBlock);
    }
  }, [blocks]);

  // Update next options based on current block
  const updateNextOptions = (block) => {
    if (!block) return;

    const options = [];
    
    if (block.type === 'event') {
      if (block.next && block.next.length > 0) {
        block.next.forEach(nextId => {
          const nextBlock = blocks.find(b => b.id === nextId);
          if (nextBlock) {
            options.push({
              type: 'next',
              targetId: nextId,
              block: nextBlock,
              label: `Continue to: ${nextBlock.title}`,
              automatic: block.autoProceed
            });
          }
        });
      }
    } else if (block.type === 'choice' && block.choices) {
      block.choices.forEach(choice => {
        if (choice.targetId) {
          const targetBlock = blocks.find(b => b.id === choice.targetId);
          if (targetBlock) {
            options.push({
              type: 'choice',
              targetId: choice.targetId,
              block: targetBlock,
              label: choice.label,
              automatic: false
            });
          }
        }
      });
    }
    
    setNextOptions(options);
  };

  // Update block progress and persistent injects
  useEffect(() => {
    if (currentBlock && currentBlock.type === 'event' && simulationRunning) {
      const pathStart = pathHistory.find(h => h.block.id === currentBlock.id);
      const blockStartTime = pathStart ? pathStart.startTime : currentTime;
      const timeInBlock = currentTime - blockStartTime;
      
      // Calculate progress
      if (currentBlock.duration) {
        const progress = Math.min(timeInBlock / currentBlock.duration, 1);
        setCurrentBlockProgress(progress);
      }
      
      // Update persistent injects
      const newPersistentInjects = [...persistentInjects];
      const currentBlockInjects = currentBlock.injects?.filter(inject => {
        const injectTime = parseInt(inject.time || 0);
        const injectTriggered = timeInBlock >= injectTime;
        const alreadyAdded = newPersistentInjects.some(
          pi => pi.blockId === currentBlock.id && 
          pi.time === inject.time && 
          pi.content === inject.content
        );
        return injectTriggered && !alreadyAdded;
      }) || [];
      
      currentBlockInjects.forEach(inject => {
        newPersistentInjects.push({
          ...inject,
          blockId: currentBlock.id,
          blockTitle: currentBlock.title,
          triggeredAt: currentTime,
          absoluteTime: blockStartTime + parseInt(inject.time || 0)
        });
      });
      
      setPersistentInjects(newPersistentInjects);

      // Check for automatic progression
      if (currentBlock.autoProceed && currentBlock.duration && currentBlockProgress >= 1) {
        const autoOption = nextOptions.find(opt => opt.automatic);
        if (autoOption) {
          handleOptionSelect(autoOption);
        }
      }
    }
  }, [currentTime, currentBlock, pathHistory, simulationRunning, nextOptions]);

  // Handle choice or automatic progression
  const handleOptionSelect = (option) => {
    const targetBlock = blocks.find(b => b.id === option.targetId);
    if (targetBlock) {
      const newPath = [...currentPath, option.targetId];
      const newHistory = [...pathHistory, {
        type: option.type,
        choice: option.type === 'choice' ? option.label : undefined,
        block: targetBlock,
        timestamp: currentTime,
        startTime: currentTime
      }];
      
      setCurrentPath(newPath);
      setCurrentBlock(targetBlock);
      setPathHistory(newHistory);
      updateNextOptions(targetBlock);
      setCurrentBlockProgress(0);
      
      if (onPathStep) {
        onPathStep(targetBlock, newPath);
      }
    }
  };

  // Reset path
  const resetPath = () => {
    const startBlocks = findStartingBlocks();
    if (startBlocks.length > 0) {
      const firstBlock = startBlocks[0];
      setCurrentBlock(firstBlock);
      setCurrentPath([firstBlock.id]);
      setPathHistory([{
        type: 'start',
        block: firstBlock,
        timestamp: 0,
        startTime: currentTime
      }]);
      updateNextOptions(firstBlock);
      setPersistentInjects([]);
      setCurrentBlockProgress(0);
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentBlock) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">No scenario available</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Path Explorer</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Current time: {formatTime(currentTime)}
            </span>
            <button
              onClick={resetPath}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Reset Path
            </button>
          </div>
        </div>
      </div>

      {/* Current Block Display */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Current Block Info */}
          <div className={`p-4 rounded-lg border-2 ${
            currentBlock.type === 'event' 
              ? 'bg-blue-50 border-blue-200' 
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-lg">{currentBlock.title}</h3>
              <div className="text-right">
                {currentBlock.type === 'event' && currentBlock.duration && (
                  <div className="text-sm text-gray-500">
                    Duration: {currentBlock.duration}s
                  </div>
                )}
                {currentBlock.type === 'event' && currentBlockProgress > 0 && (
                  <div className="text-sm text-blue-600">
                    Progress: {Math.round(currentBlockProgress * 100)}%
                  </div>
                )}
              </div>
            </div>
            
            {currentBlock.description && (
              <p className="text-gray-600 mb-3">{currentBlock.description}</p>
            )}

            {/* Progress bar for event blocks */}
            {currentBlock.type === 'event' && currentBlock.duration && currentBlockProgress > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${currentBlockProgress * 100}%` }}
                />
              </div>
            )}
            
            {/* Next Options */}
            {nextOptions.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Next Steps:</h4>
                <div className="space-y-2">
                  {nextOptions.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleOptionSelect(option)}
                      className={`w-full p-3 text-left border rounded transition-colors ${
                        option.type === 'choice' 
                          ? 'border-green-300 hover:bg-green-100' 
                          : 'border-blue-300 hover:bg-blue-100'
                      }`}
                      disabled={option.automatic && currentBlockProgress < 1}
                    >
                      <div className="font-medium">{option.label}</div>
                      {option.automatic && (
                        <div className="text-xs text-blue-600 mt-1">
                          {currentBlockProgress < 1 ? 'Waiting for event to complete...' : 'Auto-advancing now...'}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Persistent Injects Display */}
          {persistentInjects.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 mb-3">All Active Events</h3>
              <div className="space-y-2">
                {persistentInjects.map((inject, idx) => (
                  <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-amber-800">{inject.type}</div>
                        <div className="text-amber-700">{inject.content}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          From: {inject.blockTitle} @ {formatTime(inject.absoluteTime)}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {formatTime(currentTime - inject.triggeredAt)} ago
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Path History */}
          <div className="mt-6">
            <h3 className="font-medium text-gray-700 mb-3">Path History</h3>
            <div className="space-y-2">
              {pathHistory.map((step, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{step.block.title}</div>
                    <div className="text-gray-500">
                      {step.type === 'choice' && `Choice: ${step.choice}`}
                      {step.type === 'start' && 'Starting point'}
                      {step.type === 'next' && 'Auto-transition'}
                      <span className="ml-2">@ {formatTime(step.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathExplorer;